import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useIsPresent } from "framer-motion";
import { createSoapScene } from "../../lib/soapScene";
import { createBubbleScene } from "../../lib/bubbleScene";
import { audio } from "../../lib/audio";
import { breathState, startBreath } from "../../lib/breath";
import { POEM } from "../../content/poem";
import { FeelingWords } from "./FeelingWords";
import { BreathDebug } from "./BreathDebug";
import { BreathMuse } from "./BreathMuse";
import { PoemBorder } from "./PoemBorder";
import { TitleLockup } from "../TitleLockup";

const QUIET_LINK = "rounded-full border border-[#3E2F4A]/25 bg-[#FFF9F0]/90 px-5 py-1.5 font-display text-lg italic text-[#3E2F4A] shadow-[0_8px_20px_-12px_rgba(62,47,74,0.6)] underline-offset-4 transition-colors duration-200 hover:bg-white hover:underline";
import { RightsLine } from "../Rights";

// The whole poem stays on screen together, each line fading in once, softly, after the one before.
function Poem({ lines }) {
  const step = 0.55;
  return (
    <div data-testid="landing-poem" className="relative max-w-[23rem] sm:max-w-2xl">
      <PoemBorder />
      <div className="relative flex flex-col">
        {lines.map((line, i) => (
          <motion.p
            key={i}
            data-testid={`poem-line-${i}`}
            initial={{ opacity: 0, y: 5, filter: "blur(4px)" }}
            animate={{ opacity: 0.95, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, delay: 0.5 + i * step, ease: "easeOut" }}
            className="font-display text-[13px] italic leading-[1.42] text-[#4E3B63] [text-shadow:0_0_12px_rgba(255,250,246,0.95)] sm:text-[17.5px] sm:leading-[1.5]"
          >
            {line}
          </motion.p>
        ))}
      </div>
      <motion.p data-testid="poem-byline" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, delay: 0.5 + lines.length * step }} className="mt-2 font-display text-xs italic tracking-wide text-[#6E5C82]/90 sm:text-sm">
        {POEM.byline}
      </motion.p>
    </div>
  );
}

export default function WatercolorLanding({ paintings, poem, onEnter, onFeeling, onFirstTouch }) {
  const canvasRef = useRef(null);
  const fallbackRef = useRef(null);
  const sceneRef = useRef(null);
  const textRef = useRef(null);
  const touched = useRef(false);
  const present = useIsPresent();
  const lines = poem?.length ? poem : POEM.lines;
  const [mic, setMic] = useState("idle");
  const [gust, setGust] = useState(null);
  const stopRef = useRef(null);
  const touch = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const swipe = useRef(null);
  const lastGust = useRef(0);

  const blownTimer = useRef(null);
  const debug = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get("breathdebug") === "1" || q.get("debug") === "1";
  }, []);

  const breathe = useCallback((level, at = touch.current) => {
    if (level < 0.05) return;
    sceneRef.current?.blow(at.x, at.y, level);
    const now = performance.now();
    if (level > 0.3 && now - lastGust.current > 1600) {
      lastGust.current = now;
      breathState.gusts++;
      setGust({ id: now, x: at.x, y: at.y, s: level });
      setMic((m) => (m === "listening" || m === "blown" ? "blown" : m));
      clearTimeout(blownTimer.current);
      blownTimer.current = setTimeout(() => setMic((m) => (m === "blown" ? "listening" : m)), 2800);
    }
  }, []);
  const onFrame = useCallback(
    (s) => {
      const at = touch.current;
      if (s.pre > 0.12) sceneRef.current?.shimmer(at.x, at.y, s.pre);
      if (s.level > 0.05) breathe(s.level, at);
    },
    [breathe],
  );
  const stopMic = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
    clearTimeout(blownTimer.current);
    setMic((m) => (m === "listening" || m === "asking" || m === "blown" ? "idle" : m));
  }, []);
  const startMic = () => {
    setMic("asking");
    // the landing music must not play into an open mic (echo cancellation is off so a soft breath can be heard)
    window.dispatchEvent(new Event("afae-mic-on"));
    startBreath(onFrame)
      .then((stop) => {
        if (stopRef.current || !sceneRef.current) return stop();
        stopRef.current = stop;
        setMic("listening");
      })
      .catch(() => setMic("denied"));
  };
  useEffect(() => () => stopRef.current?.(), []);
  useEffect(() => {
    if (!present) stopMic();
  }, [present, stopMic]);
  useEffect(() => {
    window.__afaeBreath = { state: () => ({ ...breathState, mic }), simulate: (s = 0.8, ms = 900) => puff(touch.current, s, ms) };
    return () => delete window.__afaeBreath;
  });
  const demoBlow = (x, y) => {
    const end = performance.now() + 700;
    const id = setInterval(() => (performance.now() > end ? clearInterval(id) : sceneRef.current?.blow(x, y, 0.28)), 50);
  };
  const puff = (at, s, ms) => {
    const end = performance.now() + ms;
    const id = setInterval(() => (performance.now() > end ? clearInterval(id) : breathe(s, at)), 50);
  };

  useEffect(() => {
    const onPop = () => {
      audio.pop();
      window.dispatchEvent(new Event("afae-bubble-pop"));
    };
    const scene = createSoapScene(canvasRef.current, { onPop }) || createBubbleScene(fallbackRef.current, { onPop });
    sceneRef.current = scene;
    window.__afaeBubbles = { info: () => scene.info() };
    const zones = setInterval(() => {
      const els = [...(textRef.current?.children || []), ...document.querySelectorAll('[data-testid^="bubble-feeling-"]')];
      scene.setAvoid?.(els.map((el) => el.getBoundingClientRect()).filter((r) => r.width > 0));
    }, 400);
    return () => {
      clearInterval(zones);
      scene.destroy();
      sceneRef.current = null;
      delete window.__afaeBubbles;
    };
  }, []);

  const first = () => {
    if (touched.current) return;
    touched.current = true;
    onFirstTouch();
  };
  const down = (e) => {
    breathState.resume?.();
    touch.current = { x: e.clientX, y: e.clientY };
    swipe.current = { x: e.clientX, y: e.clientY, px: e.clientX, py: e.clientY, len: 0, t: performance.now(), done: false };
    sceneRef.current?.pointerMove(e.clientX, e.clientY);
  };
  const move = (e) => {
    sceneRef.current?.pointerMove(e.clientX, e.clientY);
    const s = swipe.current;
    if (!s || s.done || e.buttons === 0) return;
    s.len += Math.hypot(e.clientX - s.px, e.clientY - s.py);
    s.px = e.clientX;
    s.py = e.clientY;
    if (mic !== "listening" && s.len > 0.32 * Math.min(window.innerWidth, window.innerHeight) && performance.now() - s.t < 1400) {
      s.done = true;
      puff({ x: s.x, y: s.y }, 0.85, 800);
    }
  };

  return (
    <motion.div
      data-testid="entry-screen"
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      onPointerDownCapture={first}
      onPointerMove={move}
      onPointerDown={down}
      onPointerUp={() => (swipe.current = null)}
      className={`fixed inset-0 z-[70] select-none overflow-hidden bg-[#EFE3F1] ${present ? "" : "pointer-events-none"}`}
    >
      <canvas ref={canvasRef} data-testid="landing-soap-canvas" className="absolute inset-0 h-full w-full" />
      <canvas ref={fallbackRef} data-testid="landing-bubbles-canvas" className="absolute inset-0 h-full w-full touch-none" onClick={(e) => sceneRef.current?.pop(e.clientX, e.clientY)} />
      <div data-testid="landing-ink-bloom" aria-hidden="true" className="ink-bloom pointer-events-none absolute inset-0 z-[4] overflow-hidden">
        <span style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/landing/ink-bloom.webp)` }} />
        <span style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/landing/ink-bloom.webp)` }} />
      </div>
      <div className="pointer-events-none absolute inset-0 z-[5] bg-[radial-gradient(ellipse_75%_48%_at_50%_50%,rgba(255,255,255,0.72),rgba(255,255,255,0.38)_55%,transparent_78%)] sm:bg-[radial-gradient(ellipse_38%_44%_at_50%_50%,rgba(255,255,255,0.72),rgba(255,255,255,0.38)_55%,transparent_80%)]" />
      <FeelingWords paintings={paintings} avoidRef={textRef} onPick={onFeeling} gust={gust} />
      <BreathMuse mic={mic} onStart={startMic} onDemoBlow={demoBlow} />
      {debug && <BreathDebug mic={mic} />}
      <div ref={textRef} className="pointer-events-none absolute inset-0 z-20 m-auto flex h-fit max-h-full w-fit max-w-full flex-col items-center gap-[3vh] px-6 pb-[calc(3vh+150px)] pt-[3vh] text-center sm:gap-8 sm:py-[6vh]">
        <div>
          <p data-testid="landing-virtual-line" className="font-display text-base italic text-[#6E5C82]">An experience gallery</p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.6, delay: 0.2 }} className="mt-3">
            <TitleLockup size="lg" center testid="entry-title" artistTestid="entry-artist" />
          </motion.div>
        </div>
        <Poem lines={lines} />
        <div className="pointer-events-auto flex flex-col items-center gap-3">
        <button
          data-testid="enter-gallery-button"
          onClick={onEnter}
          className="pointer-events-auto rounded-full bg-[#D9C4A2]/90 px-9 py-3.5 font-display text-xl text-[#3E2F4A] shadow-[0_14px_34px_-16px_rgba(140,110,70,0.8)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#E3D0AF]"
        >
          Enter the gallery
        </button>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <a href="/about" data-testid="landing-about-link" className={`${QUIET_LINK} relative z-30 mt-1`}>
            About Priyanka ›
          </a>
        </div>
        </div>
        <RightsLine testid="rights-line-landing" className="pointer-events-auto mt-6 hidden [@media(max-height:560px)]:block" />
      </div>
    </motion.div>
  );
}
